import { VOICE_MAX_WINDOW_DAYS } from '@aica/api-types/schemas';
import { RESERVED_THEME_CODE, THEME_CODE_PATTERN } from '@aica/core/voc/theme-key';
import {
  calendarDay,
  exactWindow,
  parseObjectionsSearch,
  type ObjectionsSearch,
} from '@/components/analytics/objections/objections-search';

/**
 * Параметры адреса раздела «Голос клиента» (#848). Фильтр — те же параметры,
 * что у «Возражений» (#846): вкладка «Продажи» и есть тот экран, и смена
 * вкладки не сбрасывает период, проекты и категорию.
 *
 * `audience`, а не `tab`: во встроенном Bitrix24 `tab` уже занят вкладками
 * самой оболочки.
 */
export const VOICE_AUDIENCES = ['sales', 'marketing', 'product', 'site'] as const;
export type VoiceAudience = (typeof VOICE_AUDIENCES)[number];

export function isVoiceAudience(value: unknown): value is VoiceAudience {
  return VOICE_AUDIENCES.some((audience) => audience === value);
}

/**
 * Разделы вкладки «Продажи» (#2060, спека 2026-10-11-voice-report-layers.md
 * §2.2): первый — по умолчанию и в адрес не пишется.
 */
export const VOICE_SALES_SECTIONS = [
  'objections',
  'declines',
  'questions',
  'themes',
  // «Что изменилось» (#2394 §9 п. 7) — последним в каждой вкладке (§4 компоновки).
  'changes',
] as const;
export type VoiceSalesSection = (typeof VOICE_SALES_SECTIONS)[number];

/** Разделы вкладки «Сайт и контент» (#2072, §2.2): первый — по умолчанию. */
export const VOICE_SITE_SECTIONS = [
  'frequent',
  'firstMinute',
  'themes',
  'faq',
  'changes',
] as const;
export type VoiceSiteSection = (typeof VOICE_SITE_SECTIONS)[number];

/**
 * Виды вкладок «Маркетинг» и «Продукт и сервис» (#2394 §9 п. 7): все блоки
 * лентой или «Что изменилось» — переключатель вместо разделов.
 */
export const VOICE_AUDIENCE_SECTIONS = ['all', 'changes'] as const;
export type VoiceAudienceSection = (typeof VOICE_AUDIENCE_SECTIONS)[number];

/** Раздел любой вкладки — в адресе только не первый раздел своей вкладки. */
export type VoiceSectionId = VoiceSalesSection | VoiceSiteSection | VoiceAudienceSection;
const VOICE_SECTIONS_IN_ADDRESS: readonly VoiceSectionId[] = [
  ...new Set([...VOICE_SALES_SECTIONS.slice(1), ...VOICE_SITE_SECTIONS.slice(1)]),
];

/** Разрезы блока разбивки отказов (§2.3): первый — по умолчанию. */
export const VOICE_DECLINE_CUTS = ['callNumber', 'operator', 'stage'] as const;
export type VoiceDeclineCut = (typeof VOICE_DECLINE_CUTS)[number];

export interface VoiceSearch extends ObjectionsSearch {
  audience?: VoiceAudience;
  /** The expanded row of a tab (`kind:key`) — incl. the customer-questions blocks. */
  row?: string;
  /**
   * The open section of the tab — each tab reads only its own and falls back
   * to its first. Prefixed: the embedded Bitrix24 shell reads
   * one address with this parser and the unified dashboard's, whose `section`
   * is its own (`EmbeddedDashboardSearch`).
   */
  voiceSection?: VoiceSectionId;
  /** The cut of the declines breakdown — prefixed for the same reason. */
  voiceCut?: VoiceDeclineCut;
  /**
   * The theme filter of the whole section (#2229, spec §2.5): every block of
   * every tab counts only the conversations of this theme. A filter like the
   * period — a tab change keeps it. Prefixed for the same reason as the section.
   */
  voiceTheme?: string;
}

const oneOf = <T extends string>(values: readonly T[], value: unknown): T | undefined =>
  values.find((candidate) => candidate === value);

/**
 * Ключ строки: вид и имя конкурента / уровень / тип / код предмета вопроса или
 * причины отказа (#843, #844) или код темы (#845); конкурент — свободный текст.
 */
const ROW_PATTERN =
  /^(competitor|feedback|signal|needs|question|unanswered|firstMinute|decline|theme|noTheme):.{0,200}$/su;

/**
 * A new theme filter — picked or cleared: the expanded row and objection code
 * named something of the old scope, so they go with it.
 */
export function themeFilterPatch(voiceTheme: string | undefined): Partial<VoiceSearch> {
  return { voiceTheme, row: undefined, code: undefined };
}

export function parseVoiceSearch(search: Record<string, unknown>): VoiceSearch {
  const audience = isVoiceAudience(search.audience) ? search.audience : undefined;
  const row = typeof search.row === 'string' ? search.row : undefined;
  // The defaults stay out of the address, as the default tab does.
  const voiceSection = oneOf(VOICE_SECTIONS_IN_ADDRESS, search.voiceSection);
  const voiceCut = oneOf(VOICE_DECLINE_CUTS.slice(1), search.voiceCut);
  // A theme code's own shape (`orgThemeCodeSchema`): anything else could never
  // be a theme of the set, and would only cost a 400.
  const voiceTheme =
    typeof search.voiceTheme === 'string' &&
    THEME_CODE_PATTERN.test(search.voiceTheme) &&
    search.voiceTheme !== RESERVED_THEME_CODE
      ? search.voiceTheme
      : undefined;
  // One exact window for every tab, under the section's own limit — not the
  // one `parseObjectionsSearch` checked against the Objections screen's.
  const exact = exactWindow(search.from, search.to, VOICE_MAX_WINDOW_DAYS);
  const day = calendarDay(search.day);
  // The day label belongs to the window, so it is re-read with the window
  // under the Voice section's own limit, not the Objections one.
  const {
    from: _from,
    to: _to,
    day: _day,
    ...objections
  } = parseObjectionsSearch(search);
  return {
    ...objections,
    ...(audience ? { audience } : {}),
    ...(row && ROW_PATTERN.test(row) ? { row } : {}),
    ...(voiceSection ? { voiceSection } : {}),
    ...(voiceCut ? { voiceCut } : {}),
    ...(voiceTheme ? { voiceTheme } : {}),
    ...(exact ?? {}),
    ...(exact && day ? { day } : {}),
  };
}
